'use client';

import { useRouter } from 'next/navigation';
import { ascend, map, pipe, prop, sort } from 'ramda';
import { useMemo } from 'react';
import type { CMSGameTheme } from '@/api/cms/gameThemes';
import { getProviderIcon } from '@/helpers/cdn';
import { RestrictedProviders } from '@/helpers/conf';
import { DOMAINS } from '@/helpers/constants';
import { sortProvidersByAlphabet } from '@/helpers/game';
import type { ProviderCustom } from '@/helpers/gameCategories';
import { useGamesProviders } from '@/modules/common/hooks/useGamesProviders';
import { AVAILABLE_LOCALES } from '@/modules/localization';
import { useGetCommonStore } from '@/store';
import { useNavigation } from './navigation';

const stripLocale = (pathname: string) => {
  const [, firstSegment, ...rest] = pathname.split('/');
  return (AVAILABLE_LOCALES as string[]).includes(firstSegment)
    ? `/${rest.join('/')}`
    : pathname;
};

interface GameProvidersProps {
  providerId?: string;
  iconsMode?: 'color' | 'white';
  iconsSize?: 'small' | 'large';
  invertIcons?: boolean;
}

export const useGameProviders = ({
  providerId,
  iconsMode = 'color',
  iconsSize = 'small',
  invertIcons,
}: GameProvidersProps) => {
  const { generateLink } = useNavigation();

  const { data: dataProviders, isLoading: pendingProviders } =
    useGamesProviders();

  const providers: ProviderCustom[] = useMemo(
    () =>
      dataProviders
        .filter(
          ({ id }) =>
            id !== RestrictedProviders.LIVESPIN &&
            id !== RestrictedProviders.AUTOPAY
        )
        .map((p) => ({
          ...p,
          pathname: generateLink(`/games/all/${p.id}`),
          icon: getProviderIcon(p.id, iconsMode, iconsSize, invertIcons),
          banner: `/providers/${p.id}.webp`,
        })),
    [dataProviders, invertIcons, iconsSize, iconsMode, generateLink]
  );

  const providersSortedByAlphabet = useMemo(
    () => sortProvidersByAlphabet(providers),
    [providers]
  );

  const currentProvider = useMemo(
    () => providers.find(({ id }) => id === providerId),
    [providers, providerId]
  );

  return {
    providers,
    pendingProviders,
    currentProvider,
    providersSortedByAlphabet,
  };
};

export const useGameThemes = (categoryId?: string) => {
  const { generateLink } = useNavigation();
  const useCommonStore = useGetCommonStore();

  const gameThemes = useCommonStore.use.gameThemes();
  const formattedGameThemes = useMemo(
    () =>
      pipe(
        sort<CMSGameTheme>(ascend(prop('order'))),
        map((themes) => ({
          id: themes.id,
          categoryId: themes.categoryId,
          title: themes.title,
          icon: themes.icon,
          pathname: generateLink(`/games/${themes.categoryId}#gamesSection`),
          isActive: themes.categoryId === categoryId,
        }))
      )(gameThemes),
    [gameThemes, categoryId, generateLink]
  );

  const currentGameTheme = useMemo(
    () => formattedGameThemes.find((theme) => theme.categoryId === categoryId),
    [formattedGameThemes, categoryId]
  );

  return { gameThemes: formattedGameThemes, currentGameTheme };
};

export const useGameOpenFlow = () => {
  const { push, back } = useRouter();
  const { generateLink } = useNavigation();
  const useCommonStore = useGetCommonStore();

  const gameOpenBackUrl = useCommonStore.use.gameOpenBackUrl();

  const closeGameFlow = () => {
    if (gameOpenBackUrl) {
      push(
        gameOpenBackUrl.startsWith('/')
          ? generateLink(gameOpenBackUrl)
          : gameOpenBackUrl
      );
      return;
    }

    if (document.referrer && window.history.length > 1) {
      try {
        const referrer = new URL(document.referrer);
        const isOurDomain = Object.values(DOMAINS).some(
          (d) => referrer.hostname === d || referrer.hostname.endsWith(`.${d}`)
        );
        const isSamePageOtherLocale =
          stripLocale(referrer.pathname) ===
          stripLocale(window.location.pathname);
        if (isOurDomain && !isSamePageOtherLocale) {
          back();
          return;
        }
      } catch {}
    }

    push(generateLink('/'));
  };

  return { closeGameFlow };
};
